import _ from 'lodash';
import Vue from 'vue';
import moment from 'moment';
import reactiveCashflow from 'reactive-cashflow';

import cashflowHistory from '../utilities/cashflow-history';
import cashflowNormalizer from '../utilities/cashflow-normalizer';
import cashflowViewConsts from '../../constants/cashflow-view';

const state = {
  presentedBudgetDate: null,
  presentedBudgets: [],
  cashflowUITree: {},
  cashflow: {},
  focusIncomePrediction: false,
  remainingIncomePrediction: null,
  variableIncomeSum: null,
  currentBudgetDate: null,
  allowViewCurrentMonth: true,
};
const getters = {
  budgetDate: state => state.cashflow.budgetDate,
  presentedBudget: state => _.find(state.presentedBudgets, { budgetDate: state.presentedBudgetDate }),
  predictedBalanceAtEndOfMonth: state => -1 * state.cashflow.predictedBalance,
  remainingVariables: state => state.cashflow.variableBalancedAmount - state.cashflow.variableActualSum,
  riseupGoal: (state, getters) => (getters.remainingVariables < 0 ? state.cashflow.riseupGoal + getters.remainingVariables :
    state.cashflow.riseupGoal),
  budgetUpdatedAt: state => state.cashflow.lastUpdatedAt,
  totalIncome: state => state.cashflow.variableIncomeSum + state.cashflow.incomeEnvelopesActualSum,
  incomeEnvelopesBalanceSum: state => state.cashflow.incomeEnvelopesBalanceSum,
  totalExpense: state => state.cashflow.variableActualSum + state.cashflow.expenseEnvelopesActualSum,
  monthName: (state, getters) => moment(state.presentedBudgetDate).locale('he').format('MMM'),
  fullMonthName: (state, getters) => moment(state.presentedBudgetDate).locale('he').format('MMMM YYYY'),
  balance: state => -1 * state.cashflow.balance,
  budgetsTotals: state => _.map(state.presentedBudgets, budget => {
    const cashflow = reactiveCashflow.createCashflow(budget);
    return {
      budgetDate: budget.budgetDate,
      total: -1 * cashflow.balance,
    };
  }),
  hasExcluded: state => cashflowNormalizer.excludedByCustomerRule(state.cashflow.excluded).length > 0,
  isPredictedIncome: state => state.cashflow.isPredictedIncome,
  remainingIncomePrediction: state => -state.cashflow.remainingIncomePrediction,
  variableIncomeSum: state => state.cashflow.variableIncomeSum,
  isPresentingCurrentMonth: state => state.presentedBudgetDate === state.currentBudgetDate,
  latestBudgetDate: state => _.chain(state.presentedBudgets)
    .map(budget => budget.budgetDate)
    .sortBy()
    .last()
    .value(),
  isPresentingLatestMonth: (state, getters) => state.presentedBudgetDate === getters.latestBudgetDate,
  recurringExpenses: state => state.cashflowUITree[cashflowViewConsts.cashflowTypes.recurring],
};

const actions = {
  async initBudgets({ commit, dispatch }, { budgetDate, allowViewCurrentMonth }) {
    commit('setAllowViewCurrentMonth', allowViewCurrentMonth);
    dispatch('setCurrentBudgetDate');

    const budgetToDisplayFirst = await cashflowHistory.getBudget(budgetDate);
    commit('setPresentedBudgets', [budgetToDisplayFirst]);
    await dispatch('updatePresentedBudgetDate', budgetDate);

    dispatch('loadAllBudgets');
  },
  setCurrentBudgetDate({ commit }) {
    const currentBudgetDate = cashflowHistory.getCurrentBudgetDate();
    commit('setCurrentBudgetDate', currentBudgetDate);
  },
  async loadAllBudgets({ commit, dispatch, getters }) {
    const presentedBudgetDates = cashflowHistory.getPresentedBudgetDates();
    const budgets = await cashflowHistory.getBudgets(presentedBudgetDates);
    commit('setPresentedBudgets', budgets);
    if (!getters.presentedBudget) {
      await dispatch('updatePresentedBudgetDate', getters.latestBudgetDate);
    }
  },
  async updatePresentedBudgetDate({ commit, dispatch, getters }, budgetDate) {
    commit('setPresentedBudgetDate', budgetDate);
    await dispatch('resetBudgetTree');
  },
  async resetBudgetTree({ commit, getters }) {
    const budget = getters.presentedBudget;
    if (budget) {
      const cashflow = reactiveCashflow.createCashflow(budget);
      const cashflowUITree = cashflowNormalizer.normalizeCashflow(cashflow);
      commit('setCashflow', cashflow);
      commit('setCashflowUITree', cashflowUITree);
    }
  },
  async moveToNextBudgetMonth({ dispatch }) {
    const nextBudgetDate = cashflowHistory.getPrevBudgetDate(state.presentedBudgetDate);
    await dispatch('updatePresentedBudgetDate', nextBudgetDate);
  },
  async moveToPrevBudgetMonth({ dispatch }) {
    const nextBudgetDate = cashflowHistory.getNextBudgetDate(state.presentedBudgetDate);
    dispatch('updatePresentedBudgetDate', nextBudgetDate);
  },
  async setFocusIncomePrediction({ commit }, { focus }) {
    commit('setFocusIncomePrediction', focus);
  },
  async setOnCashflowAppTour({ commit }) {
    commit('setCashflowAppTour');
  },
};

// mutations
const mutations = {
  setCashflowOptimistic(state, newCashflow) {
    const index = _.findIndex(state.presentedBudgets, { budgetDate: state.presentedBudgetDate });
    // use Vue.set to update arrays and induce reactivity
    Vue.set(state.presentedBudgets, index, newCashflow);
  },
  setPresentedBudgets(state, presentedBudgets) {
    state.presentedBudgets = presentedBudgets; // eslint-disable-line
  },
  setCashflow(state, cashflow) {
    state.cashflow = cashflow; //eslint-disable-line
  },
  setCashflowUITree(state, cashflowUITree) {
    state.cashflowUITree = cashflowUITree; // eslint-disable-line
  },
  setFocusIncomePrediction(state, focusIncomePrediction) {
    state.focusIncomePrediction = focusIncomePrediction; // eslint-disable-line
  },
  setCashflowAppTour(state) {
    state.isPresentingTour = true; // eslint-disable-line
  },
  setPresentedBudgetDate(state, budgetDate) {
    state.presentedBudgetDate = budgetDate; // eslint-disable-line
  },
  setCurrentBudgetDate(state, budgetDate) {
    state.currentBudgetDate = budgetDate; // eslint-disable-line
  },
  setAllowViewCurrentMonth(state, allowViewCurrentMonth) {
    state.allowViewCurrentMonth = allowViewCurrentMonth; // eslint-disable-line
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/cashflowView.js